import {DataFetcher} from '@aboutyou/data-fetcher';
import {
  useActivateApiAbTestCallback,
  useIsFeatureActive,
} from '@aboutyou/feature-flags';
import {CheckoutURLV1Response} from '@aboutyou/grpc-clients/grips/protos/checkout/checkout_v1.types';
import {Timing} from '@aboutyou/mlm';
import {useBasketRoute, useHistory} from '@aboutyou/router';
import {useIsDataInfoMonitoringConsentGiven} from '@aboutyou/storage';
import {useUniqueTrackingSessionId} from '@aboutyou/tracking';
import {useCustomerSession, useUserLoginType} from '@aboutyou/user';
import React, {FC, useCallback, useEffect, useRef} from 'react';
import {useAuthenticationPrompt} from '../../features/Authentication/AuthenticationProvider/useAuthenticationPrompt';
import {CheckoutWebComponent} from './CheckoutWebComponent/CheckoutWebComponent';
import {CheckoutAnimation} from './components/CheckoutAnimation/CheckoutAnimation';
import {CheckoutErrorView} from './components/CheckoutErrorView';
import {BnplSlotsProps} from './components/PaymentSlots/BnplSlots';
import {ProductDetailsSlotsProps} from './components/ProductDetailsSlots/ProductDetailsSlots';
import {SustainabilitySlotsProps} from './components/SustainabilityBadge/SustainabilitySlots';
import {useAppUrlParams} from './hooks/useAppUrlParams';
import {CheckoutEntryDetails} from './hooks/useCheckoutEntryDetails';
import {useGetAccessTokenCallback} from './hooks/useGetAccessTokenCallback';
import {useGetCheckoutUrlRequest} from './hooks/useGetCheckoutUrl';
import {useTrackCheckout} from './hooks/useTrackCheckout';
import {useUpdateBasketState} from './hooks/useUpdateBasketState';
import {WebCheckoutDataLayerInterceptor} from './tracking/WebCheckoutDataLayerInterceptor';

export interface CheckoutViewProps
  extends SustainabilitySlotsProps,
    Pick<BnplSlotsProps, 'BnplInfoLayoutComponent'>,
    ProductDetailsSlotsProps {
  timing: Timing;
}

export const WebCheckout = (props: CheckoutViewProps) => {
  const entryTimestamp = useRef(Date.now()).current;
  useTrackCheckout({entryTimestamp, label: 'internal_checkout'});

  const request = useGetCheckoutUrlRequest({entryTimestamp});

  return (
    <>
      <WebCheckoutDataLayerInterceptor
        entryDetails={request.data?.entryDetails}
      />

      <DataFetcher
        request={request}
        View={WebCheckoutView}
        viewProps={props}
        Error={GetCheckoutUrlErrorView}
        Loading={CheckoutAnimation.Show}
      />
    </>
  );
};

const WebCheckoutView: FC<
  CheckoutURLV1Response &
    SustainabilitySlotsProps &
    Pick<BnplSlotsProps, 'BnplInfoLayoutComponent'> &
    ProductDetailsSlotsProps & {
      timing: Timing;
      entryDetails?: CheckoutEntryDetails;
    }
> = ({
  entryUrl,
  accessToken,
  authProvider,
  jwt,
  timing,
  SustainabilityInfoLayoutComponent,
  BnplInfoLayoutComponent,
  ProductDetailsLayoutComponent,
  entryDetails,
}) => {
  const correlationId = useUniqueTrackingSessionId();
  const {updateBasketState} = useUpdateBasketState();
  const showSecondaryPriceLabel = useIsFeatureActive(
    'secondary_price_label_BG',
  ).isActive;
  const isDatadogConsentGiven = useIsDataInfoMonitoringConsentGiven();

  const entryUrlObj = new URL(decodeURIComponent(entryUrl));

  const entryTimeStamp = entryUrlObj.searchParams.get('entryTimeStamp');

  const customerSession = useCustomerSession();
  const onAccessTokenRefreshFailure = useHandleAccessTokenRefreshFailure();
  const getOnInvalidAccessToken = useHandleInvalidAccessToken(
    onAccessTokenRefreshFailure,
  );

  const experiments = entryDetails?.jwtPayload?.experiments || [];

  const activateApiAbTest = useActivateApiAbTestCallback();
  useEffect(() => {
    activateApiAbTest('ab_checkout_payment_preselection_w');
  }, [activateApiAbTest]);

  return (
    <CheckoutWebComponent
      jwt={jwt}
      accessToken={accessToken!}
      authToken={customerSession?.authToken || ''}
      origin="web"
      timing={timing}
      SustainabilityInfoLayoutComponent={SustainabilityInfoLayoutComponent}
      BnplInfoLayoutComponent={BnplInfoLayoutComponent}
      ProductDetailsLayoutComponent={ProductDetailsLayoutComponent}
      entryTimeStamp={entryTimeStamp}
      correlationId={correlationId}
      updateBasketState={updateBasketState}
      onInvalidAccessToken={getOnInvalidAccessToken({
        authProvider,
      })}
      hasDatadogConsent={isDatadogConsentGiven}
      onAccessTokenRefreshFailure={onAccessTokenRefreshFailure}
      showSecondaryPriceLabel={showSecondaryPriceLabel}
      experiments={experiments}
    />
  );
};

export const WebExpressCheckout = ({timing}: {timing: Timing}) => {
  const entryTimestamp = useRef(Date.now()).current;
  useTrackCheckout({entryTimestamp, label: 'klarna_express_checkout'});

  const {updateBasketState} = useUpdateBasketState();
  const showSecondaryPriceLabel = useIsFeatureActive(
    'secondary_price_label_BG',
  ).isActive;
  const {transactionId, entryTimeStamp} = useAppUrlParams();
  const authMedium = useUserLoginType();
  const isDatadogConsentGiven = useIsDataInfoMonitoringConsentGiven();

  const onAccessTokenRefreshFailure = useHandleAccessTokenRefreshFailure();
  const getOnInvalidAccessToken = useHandleInvalidAccessToken(
    onAccessTokenRefreshFailure,
  );

  return (
    <>
      <WebCheckoutDataLayerInterceptor />

      <CheckoutWebComponent
        origin="web"
        timing={timing}
        updateBasketState={updateBasketState}
        transactionId={transactionId!}
        entryTimeStamp={entryTimeStamp}
        hasDatadogConsent={isDatadogConsentGiven}
        onInvalidAccessToken={getOnInvalidAccessToken({
          authProvider: `${authMedium}-express`,
        })}
        onAccessTokenRefreshFailure={onAccessTokenRefreshFailure}
        showSecondaryPriceLabel={showSecondaryPriceLabel}
      />
    </>
  );
};

const GetCheckoutUrlErrorView = ({error}: {error: unknown}) => {
  return <CheckoutErrorView error={error} />;
};

const useHandleInvalidAccessToken = (
  onAccessTokenRefreshError: VoidFunction,
) => {
  const getAccessToken = useGetAccessTokenCallback();
  const customerSession = useCustomerSession();

  return useCallback(
    ({authProvider}: {authProvider: string | null}) =>
      () => {
        return getAccessToken({
          authToken: customerSession?.authToken,
          authProvider,
          onAccessTokenRefreshError: () => onAccessTokenRefreshError,
        });
      },
    [customerSession?.authToken, onAccessTokenRefreshError],
  );
};

const useHandleAccessTokenRefreshFailure = () => {
  const {push} = useHistory();
  const basketPage = useBasketRoute();
  const {showUserAuthenticationPrompt} = useAuthenticationPrompt();

  return useCallback(() => {
    /**
     * When the user force log-in, the getCheckoutUrl request will rerun and in turn updates the accessToken automatically.
     */
    showUserAuthenticationPrompt({
      context: 'checkout-page-access-token-refresh-failure',
      forceSessionRefresh: true,
      needsCheckoutSession: true,
      onCancel: () => {
        push(basketPage);
      },
    });
  }, [basketPage, push, showUserAuthenticationPrompt]);
};
